import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, Settings, Eye, EyeOff, Building2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCompanies } from '../../hooks/useCompanies';
import smsService from '../../services/smsService';
import LoadingSpinner from '../common/LoadingSpinner';

const SMSSettings = () => {
  const { user } = useAuth();
  const { companies, loading: companiesLoading } = useCompanies();
  const [selectedCompany, setSelectedCompany] = useState('');
  const [settings, setSettings] = useState({
    enabled: false,
    twilioAccountSid: 'AC82c093c9dd5d370d9b2b4e984d9fe128',
    twilioAuthToken: '2605632445a3391c42f43167dfb6a7ba',
    twilioFromNumber: '+13092209254',
    templates: {
      pointsAdded: 'Olá {customerName}! Você ganhou {pointsAdded} pontos na {companyName}. Total: {totalPoints} pontos.',
      pointsRedeemed: 'Olá {customerName}! Você resgatou {pointsRedeemed} pontos na {companyName}. Total restante: {totalPoints} pontos.',
      rewardRedeemed: 'Parabéns {customerName}! Você resgatou: {rewardName} na {companyName}. Pontos restantes: {totalPoints}.'
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [showTokens, setShowTokens] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (selectedCompany) {
      loadSettings();
    }
  }, [selectedCompany]);

  const loadSettings = async () => {
    if (!selectedCompany) return;
    
    setLoading(true);
    try {
      const smsSettings = await smsService.getSMSSettings(selectedCompany);
      if (smsSettings) {
        setSettings(smsSettings);
      } else {
        // Se não há configurações, usar valores padrão com credenciais pré-configuradas
        setSettings({
          enabled: false,
          twilioAccountSid: 'AC82c093c9dd5d370d9b2b4e984d9fe128',
          twilioAuthToken: '2605632445a3391c42f43167dfb6a7ba',
          twilioFromNumber: '+13092209254',
          templates: {
            pointsAdded: 'Olá {customerName}! Você ganhou {pointsAdded} pontos na {companyName}. Total: {totalPoints} pontos.',
            pointsRedeemed: 'Olá {customerName}! Você resgatou {pointsRedeemed} pontos na {companyName}. Total restante: {totalPoints} pontos.',
            rewardRedeemed: 'Parabéns {customerName}! Você resgatou: {rewardName} na {companyName}. Pontos restantes: {totalPoints}.',
            birthday: 'Parabéns {customerName}! Hoje você faz {age} anos! 🎉 A {companyName} deseja um feliz aniversário! Você tem {totalPoints} pontos conosco.'
          }
        });
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar configurações de SMS' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedCompany) {
      setMessage({ type: 'error', text: 'Selecione uma empresa primeiro' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      const success = await smsService.saveSMSSettings(selectedCompany, settings);
      if (success) {
        setMessage({ type: 'success', text: 'Configurações salvas com sucesso!' });
      } else {
        setMessage({ type: 'error', text: 'Erro ao salvar configurações' });
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setMessage({ type: 'error', text: 'Erro ao salvar configurações' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestSMS = async () => {
    if (!testPhone.trim()) {
      setMessage({ type: 'error', text: 'Digite um número de telefone para teste' });
      return;
    }

    if (!selectedCompany) {
      setMessage({ type: 'error', text: 'Selecione uma empresa primeiro' });
      return;
    }

    setTesting(true);
    setMessage({ type: '', text: '' });

    try {
      const selectedCompanyData = companies.find(c => c.id === selectedCompany);
      const companyName = selectedCompanyData?.name || 'Empresa';
      
      const testMessage = `Teste de SMS da ${companyName}. Configuração funcionando corretamente!`;
      const result = await smsService.testSMS(testPhone, testMessage, {
        accountSid: settings.twilioAccountSid,
        authToken: settings.twilioAuthToken,
        fromNumber: settings.twilioFromNumber
      });

      if (result.success) {
        setMessage({ type: 'success', text: `SMS de teste enviado com sucesso! ID: ${result.messageId}` });
      } else {
        setMessage({ type: 'error', text: `Erro ao enviar SMS: ${result.error}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao enviar SMS de teste' });
    } finally {
      setTesting(false);
    }
  };

  const updateTemplate = (type, value) => {
    setSettings(prev => ({
      ...prev,
      templates: {
        ...prev.templates,
        [type]: value
      }
    }));
  };

  const availableVariables = [
    { key: 'customerName', description: 'Nome do cliente' },
    { key: 'companyName', description: 'Nome da empresa' },
    { key: 'pointsAdded', description: 'Pontos adicionados' },
    { key: 'pointsRedeemed', description: 'Pontos resgatados' },
    { key: 'totalPoints', description: 'Total de pontos' },
    { key: 'rewardName', description: 'Nome do prêmio' }
  ];

  if (companiesLoading || loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <MessageSquare className="w-8 h-8 text-blue-600" />
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Configurações de SMS</h2>
          <p className="text-gray-600">Configure o envio automático de SMS via Twilio para empresas</p>
        </div>
      </div>

      {/* Seletor de Empresa */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Selecionar Empresa
        </h3>
        
        <select
          value={selectedCompany}
          onChange={(e) => setSelectedCompany(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Selecione uma empresa...</option>
          {companies.map(company => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </select>
      </div>

      {!selectedCompany && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Selecione uma empresa para configurar o envio de SMS.
          </p>
        </div>
      )}

      {selectedCompany && (
        <>
          {message.text && (
            <div className={`p-4 rounded-lg ${
              message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {message.text}
            </div>
          )}

          {/* Configurações Gerais */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Configurações Gerais</h3>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="smsEnabled"
                  checked={settings.enabled}
                  onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="smsEnabled" className="text-gray-700 font-medium">
                  Habilitar envio automático de SMS para esta empresa
                </label>
              </div>
            </div>
          </div>

          {/* Configurações do Twilio */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Configurações do Twilio</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account SID
                </label>
                <input
                  type="text"
                  value={settings.twilioAccountSid}
              onChange={(e) => setSettings(prev => ({ ...prev, twilioAccountSid: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Auth Token
            </label>
            <div className="relative">
              <input
                type={showTokens ? "text" : "password"}
                value={settings.twilioAuthToken}
                onChange={(e) => setSettings(prev => ({ ...prev, twilioAuthToken: e.target.value }))}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              />
              <button
                type="button"
                onClick={() => setShowTokens(!showTokens)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showTokens ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Número Twilio (From)
            </label>
            <input
              type="text"
              value={settings.twilioFromNumber}
              onChange={(e) => setSettings(prev => ({ ...prev, twilioFromNumber: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="+1234567890"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Teste de SMS
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="(85) 98934-4054"
              />
              <button
                onClick={handleTestSMS}
                disabled={testing || !settings.twilioAccountSid || !settings.twilioAuthToken}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {testing ? <LoadingSpinner size="sm" /> : <Send className="w-4 h-4" />}
                Testar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Templates de Mensagens */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Templates de Mensagens</h3>
        
        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-800 mb-2">Variáveis Disponíveis:</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
            {availableVariables.map(variable => (
              <div key={variable.key} className="text-blue-700">
                <code className="bg-blue-100 px-2 py-1 rounded">{`{${variable.key}}`}</code>
                <span className="ml-1">{variable.description}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mensagem: Pontos Adicionados
            </label>
            <textarea
              value={settings.templates.pointsAdded}
              onChange={(e) => updateTemplate('pointsAdded', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
              placeholder="Olá {customerName}! Você ganhou {pointsAdded} pontos..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mensagem: Pontos Resgatados
            </label>
            <textarea
              value={settings.templates.pointsRedeemed}
              onChange={(e) => updateTemplate('pointsRedeemed', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
              placeholder="Olá {customerName}! Você resgatou {pointsRedeemed} pontos..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mensagem: Prêmio Resgatado
            </label>
            <textarea
              value={settings.templates.rewardRedeemed}
              onChange={(e) => updateTemplate('rewardRedeemed', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
              placeholder="Parabéns {customerName}! Você resgatou: {rewardName}..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mensagem: Aniversário 🎂
            </label>
            <textarea
              value={settings.templates.birthday || 'Parabéns {customerName}! Hoje você faz {age} anos! 🎉 A {companyName} deseja um feliz aniversário! Você tem {totalPoints} pontos conosco.'}
              onChange={(e) => updateTemplate('birthday', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
              placeholder="Parabéns {customerName}! Hoje você faz {age} anos! 🎉..."
            />
            <p className="text-xs text-gray-600 mt-1">
              Variáveis extras: {'{age}'} (idade do cliente)
            </p>
          </div>
        </div>
      </div>

      {/* Botão Salvar */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving ? <LoadingSpinner size="sm" /> : <Settings className="w-4 h-4" />}
          {saving ? 'Salvando...' : 'Salvar Configurações'}
        </button>
      </div>
        </>
      )}
    </div>
  );
};

export default SMSSettings;
